// Read/write the editable delivery-service rules (config/deliveryRules).
// Falls back to DEFAULT_DELIVERY_RULES when the doc is absent or partial.

import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../../firebase';
import { DEFAULT_DELIVERY_RULES } from '../lib/delivery';
import { DEFAULT_COLOURS, guessHex } from '../lib/colours';

const CONFIG_COLLECTION = 'config';
const DELIVERY_DOC = 'deliveryRules';
const COLOURS_DOC = 'colours';

// Shallow-merge stored rules over the defaults so a partial doc still works.
function mergeRules(data) {
  if (!data) return DEFAULT_DELIVERY_RULES;
  return {
    etsy: { ...DEFAULT_DELIVERY_RULES.etsy, ...(data.etsy || {}) },
    ebay: { ...DEFAULT_DELIVERY_RULES.ebay, ...(data.ebay || {}) },
    shopify: { ...DEFAULT_DELIVERY_RULES.shopify, ...(data.shopify || {}) },
  };
}

let cache = null;

/** Load the delivery rules (cached for the session). */
export async function getDeliveryRules() {
  if (cache) return cache;
  try {
    const snap = await getDoc(doc(db, CONFIG_COLLECTION, DELIVERY_DOC));
    cache = snap.exists() ? mergeRules(snap.data()) : DEFAULT_DELIVERY_RULES;
  } catch (e) {
    console.error('Failed to load delivery rules; using defaults', e);
    cache = DEFAULT_DELIVERY_RULES;
  }
  return cache;
}

/** Persist the delivery rules and refresh the cache. */
export async function setDeliveryRules(rules) {
  await setDoc(doc(db, CONFIG_COLLECTION, DELIVERY_DOC), rules);
  cache = rules;
}

/** Force the next getDeliveryRules() to re-read from Firestore. */
export function clearDeliveryRulesCache() {
  cache = null;
}

// ----- Colour palette (drives colour extraction for the workshop view) -----
let paletteCache = null;

// Normalise a stored palette to [{ name, hex }] (older docs stored plain names).
function normalisePalette(stored) {
  if (!Array.isArray(stored) || !stored.length) return DEFAULT_COLOURS;
  return stored.map((c) =>
    typeof c === 'string' ? { name: c, hex: guessHex(c) } : { name: c.name, hex: c.hex || guessHex(c.name) }
  );
}

/** Load the colour palette as [{ name, hex }] (cached); falls back to defaults. */
export async function getColourPalette() {
  if (paletteCache) return paletteCache;
  try {
    const snap = await getDoc(doc(db, CONFIG_COLLECTION, COLOURS_DOC));
    paletteCache = normalisePalette(snap.exists() ? snap.data().palette : null);
  } catch (e) {
    console.error('Failed to load colour palette; using default', e);
    paletteCache = DEFAULT_COLOURS;
  }
  return paletteCache;
}

/** Persist the colour palette and refresh the cache. */
export async function setColourPalette(palette) {
  await setDoc(doc(db, CONFIG_COLLECTION, COLOURS_DOC), { palette });
  paletteCache = palette;
}
